import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Loader from "../../components/Loader/Loader";
import { createItem, updateItem, getItem } from "../../utilities/items-service";
import { getTags } from "../../utilities/tags-service";
import TagSelect from "./TagSelect";
import FramingEditor from "./FramingEditor";
import TilePreview from "./TilePreview";
import ImageForm from "../../components/ImageForm/ImageForm";
import {
  normalizeAllFraming,
  normalizeImageFramings,
} from "../../utilities/framing";
import {
  KINDS,
  KIND_LABELS,
  KIND_PATHS,
  TILE_VARIANTS,
  defaultVariant,
} from "../../utilities/item-kinds";

// Article text-tile background (seal-deep) plus dark swatches that keep
// the tile's white text readable
const DEFAULT_TILE_COLOR = "#873021";
const TILE_COLOR_SWATCHES = [
  { value: "#873021", name: "Seal deep" },
  { value: "#a63a2b", name: "Seal" },
  { value: "#26221e", name: "Ink" },
  { value: "#44594e", name: "Pine" },
  { value: "#3d4e6c", name: "Indigo" },
];

const INPUT_CLASS =
  "w-full border-b border-ink/25 bg-transparent py-2 text-sm text-ink outline-none transition-colors focus:border-ink placeholder:text-ink/40";

// Collapsible group of optional fields, boxed with a tinted header bar so
// each section reads as a distinct block; the `hint` pill keeps
// populated-but-collapsed data discoverable
function FormSection({ title, hint, children }) {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-xl border border-ink/15 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 text-left cursor-pointer px-4 py-3 bg-ink/5 hover:bg-ink/10 transition-colors"
      >
        <span className="text-sm font-bold text-ink/80">{title}</span>
        <span className="flex items-center gap-2 text-xs text-ink/50">
          {hint && (
            <span className="rounded-full border border-ink/15 bg-white px-2.5 py-0.5">
              {hint}
            </span>
          )}
          <svg
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          >
            <path
              d="M5 7.5 10 12.5 15 7.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>
      {open && (
        <div className="space-y-5 px-4 pb-5 pt-4 border-t border-ink/10">
          {children}
        </div>
      )}
    </section>
  );
}

const EMPTY_FORM = {
  kind: "stamp",
  catalogue_id: "",
  catalogNumbers: "",
  subject: "",
  year: "",
  denomination: "",
  quantityIssued: "",
  designer: [],
  designerStamp: [],
  designerCachet: [],
  printingProcess: [],
  series: [],
  category: [],
  description: "",
  colours: [],
  themes: [],
  hidden: false,
  pinned: false,
  featured: false,
  tileVariant: defaultVariant("stamp"),
  framing: normalizeAllFraming(),
  imageFramings: [],
  imageCaptions: [],
  tileColor: DEFAULT_TILE_COLOR,
};

// One form for creating and editing stamps, first-day covers, and articles
export default function ItemFormPage({ user }) {
  const { catalogue_id } = useParams(); // present in edit mode only
  const isEdit = Boolean(catalogue_id);
  const navigate = useNavigate();

  // Ordered image URLs; the first one is the primary image
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Options for the tag fields, keyed by tag type; the three designer
  // fields share the "designer" list
  const [tagOptions, setTagOptions] = useState({
    theme: [],
    colour: [],
    category: [],
    series: [],
    designer: [],
    printingProcess: [],
  });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [itemId, setItemId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchData() {
      const [
        themes,
        colours,
        categories,
        seriesList,
        designers,
        printingProcesses,
        itemResponse,
      ] = await Promise.all([
        getTags("theme"),
        getTags("colour"),
        getTags("category"),
        getTags("series"),
        getTags("designer"),
        getTags("printingProcess"),
        isEdit ? getItem(catalogue_id) : Promise.resolve(null),
      ]);

      setTagOptions({
        theme: Array.isArray(themes) ? themes : [],
        colour: Array.isArray(colours) ? colours : [],
        category: Array.isArray(categories) ? categories : [],
        series: Array.isArray(seriesList) ? seriesList : [],
        designer: Array.isArray(designers) ? designers : [],
        printingProcess: Array.isArray(printingProcesses)
          ? printingProcesses
          : [],
      });

      if (isEdit) {
        if (
          itemResponse &&
          typeof itemResponse === "object" &&
          itemResponse._id
        ) {
          setFormData({
            kind: itemResponse.kind || "stamp",
            catalogue_id: itemResponse.catalogue_id || "",
            catalogNumbers: itemResponse.catalogNumbers || "",
            subject: itemResponse.subject || "",
            year: itemResponse.year || "",
            denomination: itemResponse.denomination || "",
            quantityIssued: itemResponse.quantityIssued || "",
            // Tag refs arrive populated — keep just the ids in the form
            designer: itemResponse.designer?.map((t) => t._id) || [],
            designerStamp: itemResponse.designerStamp?.map((t) => t._id) || [],
            designerCachet:
              itemResponse.designerCachet?.map((t) => t._id) || [],
            printingProcess:
              itemResponse.printingProcess?.map((t) => t._id) || [],
            series: itemResponse.series?.map((t) => t._id) || [],
            category: itemResponse.category?.map((t) => t._id) || [],
            description: itemResponse.description || "",
            colours: itemResponse.colours?.map((c) => c._id) || [],
            themes: itemResponse.themes?.map((t) => t._id) || [],
            hidden: itemResponse.hidden || false,
            pinned: itemResponse.pinned || false,
            featured: itemResponse.featured || false,
            tileVariant:
              itemResponse.tileVariant ||
              defaultVariant(itemResponse.kind || "stamp"),
            framing: normalizeAllFraming(itemResponse.framing),
            imageFramings: normalizeImageFramings(itemResponse.imageFramings),
            imageCaptions: (itemResponse.imageCaptions || []).filter(
              (entry) => typeof entry?.url === "string" && entry.url,
            ),
            tileColor: itemResponse.tileColor || DEFAULT_TILE_COLOR,
          });
          // Older items only have the single `image` field
          setImages(
            itemResponse.images?.length
              ? itemResponse.images
              : itemResponse.image
                ? [itemResponse.image]
                : [],
          );
          setItemId(itemResponse._id);
        } else {
          console.log("error fetching item:", itemResponse);
          setError("Could not load this item.");
        }
      }
      setIsLoading(false);
    }
    fetchData();
  }, [catalogue_id, isEdit]);

  // The ID becomes a URL segment — letters, digits, hyphens, and
  // underscores only (existing IDs like "P211v2" and "seed-*" all fit)
  const catalogueIdInvalid =
    formData.catalogue_id !== "" &&
    !/^[A-Za-z0-9_-]+$/.test(formData.catalogue_id);
  const CATALOGUE_ID_MESSAGE =
    "Catalogue ID can only contain letters, numbers, hyphens, and underscores — no spaces or special characters.";
  const imageRequired = formData.kind !== "article";
  // The primary image drives the tile preview and framing editor
  const imageUrl = images[0] || null;

  function selectKind(kind) {
    setFormData({
      ...formData,
      kind,
      tileVariant: defaultVariant(kind),
    });
  }

  async function handleSubmit(evt) {
    evt.preventDefault();
    // The inputs aren't inside a <form>, so their `required` attributes
    // never fire — enforce the required fields here instead
    if (!formData.catalogue_id.trim()) {
      setError("Catalogue ID is required.");
      return;
    }
    if (catalogueIdInvalid) {
      setError(CATALOGUE_ID_MESSAGE);
      return;
    }
    if (formData.kind === "article") {
      if (!formData.description.trim()) {
        setError("Body is required.");
        return;
      }
    } else {
      // Stamps and covers: catalogue no., subject, and date of issue are
      // mandatory; description is optional
      if (!formData.catalogNumbers.trim()) {
        setError("Catalogue number is required.");
        return;
      }
      if (!formData.subject.trim()) {
        setError("Subject is required.");
        return;
      }
      if (!formData.year.trim()) {
        setError("Date of issue is required.");
        return;
      }
    }
    try {
      setIsSubmitting(true);
      setError("");
      const finalFormData = { ...formData };
      finalFormData.images = images;
      finalFormData.image = images[0] || "";
      // Drop framings for images that were removed or promoted to primary
      // (the primary's detail framing lives in framing.detail)
      finalFormData.imageFramings = formData.imageFramings.filter((entry) =>
        images.slice(1).includes(entry.url),
      );
      // Drop captions for removed images and blank captions
      finalFormData.imageCaptions = formData.imageCaptions.filter(
        (entry) => images.includes(entry.url) && entry.caption.trim(),
      );
      const result = isEdit
        ? await updateItem(itemId, finalFormData)
        : await createItem(finalFormData);
      if (result instanceof Error) {
        if (result.status === 409) {
          setError(
            "This Catalogue ID is already in use. Please choose a different one.",
          );
        } else {
          setError("Something went wrong. Please try again.");
        }
        setIsSubmitting(false);
        return;
      }
      if (isEdit) {
        navigate(
          `/${KIND_PATHS[formData.kind]}/${formData.catalogue_id || catalogue_id}`,
        );
      } else {
        navigate("/");
      }
    } catch (err) {
      console.log(err);
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  }

  function handleChange(evt) {
    setFormData({ ...formData, [evt.target.name]: evt.target.value });
  }

  // Captions are keyed by image URL, like imageFramings, so reordering
  // the images can't desync them
  function handleCaptionChange(url, caption) {
    setFormData((prev) => ({
      ...prev,
      imageCaptions: [
        ...prev.imageCaptions.filter((entry) => entry.url !== url),
        { url, caption },
      ],
    }));
  }

  // A TagSelect created a new tag — add it to that type's options
  function handleTagCreated(type, tag) {
    setTagOptions((prev) => ({ ...prev, [type]: [...prev[type], tag] }));
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  if (!user?.isAdmin) {
    return (
      <div className="text-center py-20">
        <p className="text-ink/60 mb-4">Not authorised</p>
        <button
          className="text-sm underline text-ink"
          onClick={() => navigate("/")}
        >
          Return to home
        </button>
      </div>
    );
  }

  // Edit mode but the item never loaded — saving would PUT /items/null
  if (isEdit && !itemId) {
    return (
      <div className="text-center py-20">
        <p className="text-ink/60 mb-4">Could not load this item.</p>
        <button
          className="text-sm underline text-ink"
          onClick={() => navigate("/")}
        >
          Return to home
        </button>
      </div>
    );
  }

  const kindLabel = KIND_LABELS[formData.kind].toLowerCase();
  const isArticle = formData.kind === "article";

  // Section summaries — drive the "n filled" hints in the section headers
  const detailsCount = [
    ...(isArticle ? [formData.catalogNumbers] : []),
    formData.denomination,
    formData.quantityIssued,
    formData.designer,
    formData.designerStamp,
    formData.designerCachet,
    formData.printingProcess,
    formData.series,
    formData.category,
  ].filter((value) =>
    Array.isArray(value) ? value.length > 0 : value && value.trim(),
  ).length;
  const tagsCount = formData.themes.length + formData.colours.length;
  const hasDescription = Boolean(formData.description.trim());
  const displayCustomised =
    formData.featured ||
    formData.tileVariant !== defaultVariant(formData.kind) ||
    (isArticle && formData.tileColor !== DEFAULT_TILE_COLOR) ||
    formData.imageFramings.length > 0 ||
    JSON.stringify(formData.framing) !== JSON.stringify(normalizeAllFraming());

  return (
    // On md+ the page fills the viewport below the header: the card scrolls
    // internally while the heading and action buttons stay in view.
    // On lg+ the form column sits on the left with the live preview in the
    // remaining space to its right, so the preview survives narrower screens
    <div className="py-3 px-4 md:h-[calc(100vh-88px)] lg:flex lg:justify-evenly lg:gap-8">
      <div className="w-full max-w-2xl mx-auto lg:mx-0 lg:shrink-0 md:h-full md:flex md:flex-col">
      <h1 className="font-display text-2xl tracking-tight text-ink mb-2 shrink-0">
        {isEdit ? `Edit ${kindLabel}` : "New item"}
      </h1>
      <div className="bg-white rounded-2xl shadow-sm border border-ink/10 p-8 md:flex-1 md:min-h-0 md:overflow-y-auto">
        <div className="space-y-5">
          {/* hidden — kept at the top so an item's visibility is obvious
              the moment the form opens */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="hidden"
              name="hidden"
              checked={formData.hidden}
              onChange={(evt) =>
                setFormData({ ...formData, hidden: evt.target.checked })
              }
              className="h-4 w-4 rounded border-ink/25 accent-seal"
            />
            <label htmlFor="hidden" className="text-sm font-bold text-ink/80">
              Hidden — don&apos;t show this item on the site
            </label>
          </div>

          {/* pinned */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="pinned"
              name="pinned"
              checked={formData.pinned}
              onChange={(evt) =>
                setFormData({ ...formData, pinned: evt.target.checked })
              }
              className="h-4 w-4 rounded border-ink/25 accent-seal"
            />
            <label htmlFor="pinned" className="text-sm font-bold text-ink/80">
              Pinned — keep this item at the start of the grid
            </label>
          </div>

          {/* kind selector */}
          <div>
            <p className="text-sm font-bold text-ink/80 mb-2">
              Type{" "}
              {isEdit && (
                <span className="text-ink/40 font-normal">
                  (cannot be changed)
                </span>
              )}
            </p>
            <div className="flex gap-2">
              {KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  disabled={isEdit}
                  onClick={() => selectKind(kind)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                    formData.kind === kind
                      ? "bg-ink text-paper border-ink"
                      : "bg-white text-ink/60 border-ink/25 hover:border-ink/60"
                  } ${isEdit && formData.kind !== kind ? "opacity-30" : ""} ${isEdit ? "cursor-default" : "cursor-pointer"}`}
                >
                  {KIND_LABELS[kind]}
                </button>
              ))}
            </div>
          </div>

          {/* upload image */}
          <div>
            <p className="text-sm font-bold text-ink/80 mb-2">
              Image(s){" "}
              {imageRequired ? (
                <span className="text-seal font-normal">
                  (At least one required)
                </span>
              ) : (
                <span className="text-ink/40 font-normal">(Optional)</span>
              )}
            </p>
            <div>
              <ImageForm images={images} setImages={setImages} />
            </div>
          </div>

          {/* catalogue ID */}
          <div>
            <label className="block text-sm font-bold text-ink/80 mb-0.5">
              Catalogue ID{" "}
              <span className="text-seal font-normal">(Required)</span>
            </label>
            <p className="text-xs text-ink/40 mb-1">
              This is what appears in the URL (e.g., 733 would result in /
              {KIND_PATHS[formData.kind]}/773). Letters, numbers, hyphens, and
              underscores only.
            </p>
            <input
              type="text"
              name="catalogue_id"
              value={formData.catalogue_id}
              onChange={handleChange}
              required
              placeholder="Unique catalogue identifier"
              className={`w-full border-b bg-transparent py-2 text-sm text-ink outline-none transition-colors focus:border-ink placeholder:text-ink/40 ${catalogueIdInvalid ? "border-seal" : "border-ink/25"}`}
            />
            {catalogueIdInvalid && (
              <p className="text-xs text-seal mt-1">{CATALOGUE_ID_MESSAGE}</p>
            )}
          </div>

          {/* subject / title, paired with catalog numbers for stamps and covers */}
          <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 items-start">
            <div className={isArticle ? "sm:col-span-2" : ""}>
              <label className="block text-sm font-bold text-ink/80 mb-1">
                {isArticle ? "Title" : "Subject"}{" "}
                {!isArticle && (
                  <span className="text-seal font-normal">(Required)</span>
                )}
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className={INPUT_CLASS}
              />
            </div>
            {!isArticle && (
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Catalogue number{" "}
                  <span className="text-seal font-normal">(Required)</span>
                </label>
                <input
                  type="text"
                  name="catalogNumbers"
                  value={formData.catalogNumbers}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>
            )}
          </div>

          {/* date of issue */}
          <div>
            <label className="block text-sm font-bold text-ink/80 mb-0.5">
              Date of issue{" "}
              {!isArticle && (
                <span className="text-seal font-normal">(Required)</span>
              )}
            </label>
            <p className="text-xs text-ink/40 mb-1">
              Enter as YYYY-MM-DD, YYYY-MM, or YYYY — full dates display as
              &ldquo;April 6, 1967&rdquo;.
            </p>
            <input
              type="text"
              name="year"
              value={formData.year}
              onChange={handleChange}
              placeholder="e.g. 1967-04-06, 1965, or unknown"
              className={INPUT_CLASS}
            />
          </div>

          {/* article body — required, so it stays outside the collapsible
              sections; stamps and covers get a Description section below */}
          {isArticle && (
            <div>
              <label className="block text-sm font-bold text-ink/80 mb-1">
                Body <span className="text-seal font-normal">(Required)</span>
              </label>
              <textarea
                name="description"
                rows={16}
                value={formData.description}
                onChange={handleChange}
                required
                className="w-full min-h-[195px] border border-ink/25 rounded-lg bg-transparent p-3 text-sm text-ink outline-none transition-colors focus:border-ink placeholder:text-ink/40 resize-y"
              ></textarea>
              <p className="text-xs text-ink/40 mt-1">
                Supports formatting: **bold**, *italic*, [link text](url)
              </p>
            </div>
          )}

          {/* optional catalogue metadata */}
          <FormSection
            title="Additional details"
            hint={detailsCount > 0 ? `${detailsCount} filled` : "empty"}
          >
            <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 items-start">
              {isArticle && (
                <div>
                  <label className="block text-sm font-bold text-ink/80 mb-1">
                    Catalogue number
                  </label>
                  <input
                    type="text"
                    name="catalogNumbers"
                    value={formData.catalogNumbers}
                    onChange={handleChange}
                    className={INPUT_CLASS}
                  />
                </div>
              )}

              {/* denomination */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Denomination
                </label>
                <input
                  type="text"
                  name="denomination"
                  value={formData.denomination}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>

              {/* quantity issued */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Quantity issued
                </label>
                <input
                  type="text"
                  name="quantityIssued"
                  value={formData.quantityIssued}
                  onChange={handleChange}
                  className={INPUT_CLASS}
                />
              </div>

              {/* designer — the three designer fields share one tag list */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Designer
                </label>
                <TagSelect
                  type="designer"
                  values={formData.designer}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, designer: ids }))
                  }
                  options={tagOptions.designer}
                  onCreated={(tag) => handleTagCreated("designer", tag)}
                />
              </div>

              {/* designer (stamp) */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Designer (stamp)
                </label>
                <TagSelect
                  type="designer"
                  values={formData.designerStamp}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, designerStamp: ids }))
                  }
                  options={tagOptions.designer}
                  onCreated={(tag) => handleTagCreated("designer", tag)}
                />
              </div>

              {/* designer (cachet) */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Designer (cachet)
                </label>
                <TagSelect
                  type="designer"
                  values={formData.designerCachet}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, designerCachet: ids }))
                  }
                  options={tagOptions.designer}
                  onCreated={(tag) => handleTagCreated("designer", tag)}
                />
              </div>

              {/* printing process */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Printing process
                </label>
                <TagSelect
                  type="printingProcess"
                  values={formData.printingProcess}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, printingProcess: ids }))
                  }
                  options={tagOptions.printingProcess}
                  onCreated={(tag) => handleTagCreated("printingProcess", tag)}
                />
              </div>

              {/* series */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Series
                </label>
                <TagSelect
                  type="series"
                  values={formData.series}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, series: ids }))
                  }
                  options={tagOptions.series}
                  onCreated={(tag) => handleTagCreated("series", tag)}
                />
              </div>

              {/* category */}
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Category
                </label>
                <TagSelect
                  type="category"
                  values={formData.category}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, category: ids }))
                  }
                  options={tagOptions.category}
                  onCreated={(tag) => handleTagCreated("category", tag)}
                />
              </div>

            </div>
          </FormSection>

          {/* description — stamps and covers only (article body is above) */}
          {!isArticle && (
            <FormSection
              title="Description"
              hint={hasDescription ? "filled" : "empty"}
            >
              <div>
                <textarea
                  name="description"
                  rows={8}
                  value={formData.description}
                  onChange={handleChange}
                  className="w-full min-h-[195px] border border-ink/25 rounded-lg bg-transparent p-3 text-sm text-ink outline-none transition-colors focus:border-ink placeholder:text-ink/40 resize-y"
                ></textarea>
                <p className="text-xs text-ink/40 mt-1">
                  Supports formatting: **bold**, *italic*, [link text](url)
                </p>
              </div>
            </FormSection>
          )}

          {/* homepage tile, framing, and visibility */}
          <FormSection
            title="Homepage display"
            hint={displayCustomised ? "customised" : "defaults"}
          >
            {/* featured */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={(evt) =>
                  setFormData({ ...formData, featured: evt.target.checked })
                }
                className="h-4 w-4 rounded border-ink/25 accent-seal"
              />
              <label
                htmlFor="featured"
                className="text-sm font-bold text-ink/80"
              >
                Featured — show in the homepage carousel
              </label>
            </div>

            {/* tile format */}
            <div>
              <label className="block text-sm font-bold text-ink/80 mb-1">
                Tile format on the homepage mosaic
              </label>
              <select
                name="tileVariant"
                value={formData.tileVariant}
                onChange={handleChange}
                className="w-full border-b border-ink/25 bg-transparent py-2 text-sm text-ink outline-none transition-colors focus:border-ink"
              >
                {TILE_VARIANTS[formData.kind].map((variant) => (
                  <option key={variant.value} value={variant.value}>
                    {variant.label}
                  </option>
                ))}
              </select>
            </div>

            {/* tile colour — background of the article text tile */}
            {isArticle && (
              <div>
                <label className="block text-sm font-bold text-ink/80 mb-1">
                  Tile colour
                </label>
                <p className="text-xs text-ink/40 mb-2">
                  Background for the text tile; dark colours keep the text
                  readable.
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    name="tileColor"
                    value={formData.tileColor}
                    onChange={handleChange}
                    aria-label="Tile colour"
                    className="h-9 w-12 cursor-pointer rounded border border-ink/25 bg-transparent p-0.5"
                  />
                  {TILE_COLOR_SWATCHES.map((swatch) => (
                    <button
                      key={swatch.value}
                      type="button"
                      title={swatch.name}
                      aria-label={`Tile colour ${swatch.name}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          tileColor: swatch.value,
                        }))
                      }
                      className={`h-7 w-7 rounded-full border-2 transition-colors ${
                        formData.tileColor === swatch.value
                          ? "border-ink"
                          : "border-transparent hover:border-ink/40"
                      }`}
                      style={{ backgroundColor: swatch.value }}
                    />
                  ))}
                  <span className="font-mono text-xs text-ink/60 ml-1">
                    {formData.tileColor}
                  </span>
                </div>
              </div>
            )}

            {/* homepage tile preview — inline fallback for screens too
                narrow for the side panel */}
            <div className="lg:hidden">
              <TilePreview formData={formData} imageUrl={imageUrl} />
            </div>

            {/* image framing — needs an uploaded image */}
            {imageUrl && (
              <FramingEditor
                images={images}
                kind={formData.kind}
                tileVariant={formData.tileVariant}
                featured={formData.featured}
                framing={formData.framing}
                imageFramings={formData.imageFramings}
                onChange={(framing) =>
                  setFormData((prev) => ({ ...prev, framing }))
                }
                onImageFramingsChange={(imageFramings) =>
                  setFormData((prev) => ({ ...prev, imageFramings }))
                }
                captions={
                  formData.kind === "article"
                    ? formData.imageCaptions
                    : undefined
                }
                onCaptionChange={
                  formData.kind === "article" ? handleCaptionChange : undefined
                }
              />
            )}

          </FormSection>

          {/* themes & colours */}
          <FormSection
            title="Themes & colours"
            hint={tagsCount > 0 ? `${tagsCount} selected` : "none"}
          >
            <div className="grid gap-x-6 gap-y-5 sm:grid-cols-2 items-start">
              {/* themes */}
              <div>
                <p className="text-sm font-bold text-ink/80 mb-1">Themes</p>
                <TagSelect
                  type="theme"
                  values={formData.themes}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, themes: ids }))
                  }
                  options={tagOptions.theme}
                  onCreated={(tag) => handleTagCreated("theme", tag)}
                />
              </div>

              {/* colours */}
              <div>
                <p className="text-sm font-bold text-ink/80 mb-1">Colours</p>
                <TagSelect
                  type="colour"
                  values={formData.colours}
                  onChange={(ids) =>
                    setFormData((prev) => ({ ...prev, colours: ids }))
                  }
                  options={tagOptions.colour}
                  onCreated={(tag) => handleTagCreated("colour", tag)}
                />
              </div>
            </div>
          </FormSection>
        </div>
      </div>

      {/* error message — outside the scroll area so it's visible next to
          the buttons when validation fails */}
      {error && (
        <div className="mt-2 shrink-0 rounded-lg bg-seal/10 border border-seal/30 px-4 py-2 text-sm text-seal-deep">
          {error}
        </div>
      )}

      {/* submit buttons — below the card, always in view on md+ */}
      <div className="flex gap-3 mt-3 shrink-0">
        {isEdit && (
          <button
            onClick={() =>
              navigate(`/${KIND_PATHS[formData.kind]}/${catalogue_id}`)
            }
            className="flex-1 rounded-lg border border-ink/25 py-2.5 text-sm font-medium text-ink/70 transition-colors hover:border-ink/60 hover:text-ink"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={
            (imageRequired && images.length === 0) ||
            isSubmitting ||
            catalogueIdInvalid
          }
          className="flex-1 rounded-lg bg-ink py-2.5 text-sm font-medium text-paper transition-colors hover:bg-ink/85 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <Loader />
          ) : isEdit ? (
            "Save changes"
          ) : (
            `Add ${kindLabel}`
          )}
        </button>
      </div>
      </div>

      {/* Live preview of the homepage tile; vertically centred beside the
          form on lg+ screens, with the free space spread around both columns */}
      <aside className="hidden lg:flex shrink-0 w-[235px] items-center">
        <div className="w-full">
          <TilePreview formData={formData} imageUrl={imageUrl} />
        </div>
      </aside>
    </div>
  );
}
